/**
 * LeadSuccessReviewCta — appears ONLY on a real lead-submit success.
 *
 * Behavior:
 *  - Mounts globally (in App.js).
 *  - Listens for `ssc:lead-submitted` event from LeadForm success.
 *  - Opens a modal: "How was your experience with Scott so far?" → Leave a Review / Maybe Later.
 *  - After clicking Leave a Review, opens Scott's review page in a new tab,
 *    then asks the existing follow-up modal for Byers Chevrolet.
 *  - Dismiss persists to sessionStorage so the user is not nagged.
 *
 * Strict rules (per dealer/legal):
 *  - No 5-star wording.
 *  - No rewards/incentives.
 *  - No review gating ("only happy customers").
 *  - Hide if dismissed for that visitor/session.
 */
import { useEffect, useRef, useState } from "react";
import { Star, X } from "lucide-react";
import { siteConfig } from "../config";
import {
  markReviewCtaDismissed,
  onLeadSubmitted,
  wasReviewCtaDismissed,
} from "../lib/leadEvents";

export default function LeadSuccessReviewCta() {
  const [step, setStep] = useState(null); // null | 'ask' | 'followup'
  const detailRef = useRef(null);

  useEffect(() => {
    const off = onLeadSubmitted((detail) => {
      if (wasReviewCtaDismissed()) return;
      detailRef.current = detail;
      // Defer slightly so the success message itself can render first.
      setTimeout(() => setStep("ask"), 600);
    });
    return off;
  }, []);

  if (!step) return null;

  const close = () => {
    markReviewCtaDismissed();
    setStep(null);
  };

  const openScottReview = () => {
    try {
      window.open(siteConfig.links.scottReviewUrl, "_blank", "noopener,noreferrer");
    } catch {}
    setTimeout(() => setStep("followup"), 250);
  };

  const openByersReview = () => {
    try {
      window.open(siteConfig.links.byersReviewUrl, "_blank", "noopener,noreferrer");
    } catch {}
    close();
  };

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/45 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="lead-review-cta-modal"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 relative">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 text-[var(--ssc-ink-2)] hover:text-[var(--ssc-ink)]"
          data-testid="lead-review-cta-close"
        >
          <X size={18} />
        </button>

        {step === "ask" && (
          <div data-testid="lead-review-cta-ask">
            <div className="flex items-center gap-2">
              <Star className="text-[var(--ssc-blue)]" size={20} />
              <h3 className="text-xl font-extrabold text-[var(--ssc-navy)]">
                How was your experience with Scott so far?
              </h3>
            </div>
            <p className="mt-3 text-sm text-[var(--ssc-ink-2)] leading-relaxed">
              If Scott helped you, you&apos;re welcome to share your experience in a
              Google review. Your feedback helps other shoppers know what to expect.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openScottReview}
                className="ssc-btn ssc-btn-primary"
                data-testid="lead-review-cta-leave"
              >
                Leave a Review
              </button>
              <button
                type="button"
                onClick={close}
                className="ssc-btn ssc-btn-outline"
                data-testid="lead-review-cta-later"
              >
                Maybe Later
              </button>
            </div>
            <p className="mt-4 text-[10px] text-[var(--ssc-ink-2)]">
              Honest feedback only — Scott appreciates all reviews, good or otherwise.
            </p>
          </div>
        )}

        {step === "followup" && (
          <div data-testid="lead-review-cta-followup">
            <h3 className="text-xl font-extrabold text-[var(--ssc-navy)]">
              Thank you for leaving Scott a review.
            </h3>
            <p className="mt-3 text-sm text-[var(--ssc-ink-2)] leading-relaxed">
              Would you also like to review Byers Chevrolet on Google?
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openByersReview}
                className="ssc-btn ssc-btn-primary"
                data-testid="lead-review-cta-byers-yes"
              >
                Yes — Review Byers
              </button>
              <button
                type="button"
                onClick={close}
                className="ssc-btn ssc-btn-outline"
                data-testid="lead-review-cta-byers-no"
              >
                No Thanks
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
